// SPDX-FileCopyrightText: 2023-2025 Open Pioneer project (https://github.com/open-pioneer)
// SPDX-License-Identifier: Apache-2.0
import {
    ChakraProvider,
    createSystem,
    defaultConfig,
    defineConfig,
    EnvironmentProvider,
    LocaleProvider,
    mergeConfigs,
    SystemConfig,
    SystemStyleObject
} from "@chakra-ui/react";
import { ReadonlyReactive } from "@conterra/reactivity-core";
import createCache, { EmotionCache } from "@emotion/cache";
import { CacheProvider, Global } from "@emotion/react";
import { config as defaultTrailsConfig } from "@open-pioneer/base-theme";
import { Error } from "@open-pioneer/core";
import { useReactiveSnapshot } from "@open-pioneer/reactivity";
import { FC, PropsWithChildren, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { APP_ROOT_CLASS, getBuiltinStyles, getStylesRoot, RootNode } from "../dom";
import { ErrorId } from "../errors";
import { ColorModeValue } from "../api";

/** @internal */
export type CustomChakraProviderProps = PropsWithChildren<{
    /**
     * Document root node.
     */
    rootNode: RootNode;

    /**
     * Web component root.
     *
     * NOTE: undefined for test support only.
     */
    hostNode?: HTMLElement;

    /**
     * Application container (react's render target).
     */
    appRoot: HTMLElement;

    /**
     * Chakra system config (can be used to provide custom theme).
     */
    config?: ReadonlyReactive<SystemConfig | undefined>;

    /**
     * Application locale for chakra's `LocaleProvider`.
     */
    locale?: string;

    /**
     * Custom color mode value.
     */
    colorMode: ReadonlyReactive<ColorModeValue>;

    /**
     * Custom CSS used by the application.
     */
    styles?: ReadonlyReactive<string>;
}>;

const APP_ROOT_CSS = `.${APP_ROOT_CLASS}`;

/**
 * Wraps the entire react application and configures chakra-ui (styling etc.).
 *
 * Exported so it can be used from the test-utils package.
 *
 * @internal
 */
export const CustomChakraProvider: FC<CustomChakraProviderProps> = ({
    rootNode,
    appRoot,
    hostNode,
    children,
    config,
    locale = "en-US",
    colorMode,
    styles
}) => {
    /*
        Setup chakra integration:

        1. Mount styles into the shadow root via emotion
        2. Use chakra's EnvironmentProvider to tell it about the shadow root context
        3. Setup global styles correctly (selectors etc.) via chakra's system object
        4. Render the rest of the application
    */

    const systemConfig = useReactiveSnapshot(() => config?.value ?? defaultTrailsConfig, [config]);

    const system = useMemo(() => {
        const mergedConfig = mergeConfigs(defaultConfig, systemConfig);
        return createSystem(
            mergedConfig,

            // https://www.chakra-ui.com/docs/get-started/environments/shadow-dom
            defineConfig({
                preflight: {
                    scope: APP_ROOT_CSS
                },
                cssVarsRoot: APP_ROOT_CSS,
                conditions: redirectLightCondition(mergedConfig.conditions),
                globalCss: redirectHtmlProps(mergedConfig.globalCss)
            })
        );
    }, [systemConfig]);

    const effectiveColorMode = useReactiveSnapshot(() => colorMode.value, [colorMode]);

    useLayoutEffect(() => {
        const classes = appRoot.classList;
        classes.toggle("light", effectiveColorMode === "light");
        classes.toggle("dark", effectiveColorMode === "dark");
        appRoot.style.colorScheme = effectiveColorMode;
    }, [appRoot, effectiveColorMode]);

    const cache = useEmotionCache(rootNode);
    return (
        cache && (
            <CacheProvider value={cache}>
                <EnvironmentProvider
                    value={rootNode}
                    // Patched property
                    portalNode={appRoot}
                >
                    <LocaleProvider locale={locale}>
                        <ChakraProvider value={system}>
                            <GlobalStyles rootNode={rootNode} hostNode={hostNode} styles={styles} />
                            {children}
                        </ChakraProvider>
                    </LocaleProvider>
                </EnvironmentProvider>
            </CacheProvider>
        )
    );
};

function GlobalStyles(props: {
    rootNode: RootNode;
    hostNode: HTMLElement | undefined;
    styles: ReadonlyReactive<string> | undefined;
}) {
    const { rootNode, hostNode, styles } = props;
    const builtinStyles = useMemo(() => {
        const css = getBuiltinStyles(rootNode, hostNode);
        return <Global styles={css} />;
    }, [rootNode, hostNode]);
    const appStyles = useReactiveSnapshot(() => {
        const css = styles?.value;
        return css && <Global styles={css} />;
    }, [styles]);
    return (
        <>
            {builtinStyles}
            {appStyles}
        </>
    );
}

function redirectHtmlProps(
    css: Record<string, SystemStyleObject> | undefined
): Record<string, SystemStyleObject> {
    const { html, ...rest } = css ?? {};
    if (!html) {
        throw new Error(ErrorId.INTERNAL, "Expected global rules on html element.");
    }
    // Take default html styles and apply them to the host element instead
    return {
        ...rest,
        [APP_ROOT_CSS]: html
    };
}

function redirectLightCondition(
    conditions: Record<string, unknown> | undefined
): Record<string, unknown> {
    // Make sure light mode tokens are applied
    return {
        ...conditions,
        // Before: ":root &, .light &"
        light: `${APP_ROOT_CSS} &, .light &`
    };
}

function useEmotionCache(rootNode: RootNode): EmotionCache | undefined {
    const stylesRoot = useMemo(() => getStylesRoot(rootNode), [rootNode]);

    const [cache, setCache] = useState<EmotionCache | undefined>();
    useEffect(() => {
        const cache = createCache({
            key: "css",
            container: stylesRoot
        });

        // Disable useless warnings like `The pseudo class ":nth-child" is potentially unsafe when doing server-side rendering. Try changing it to ":nth-of-type".`
        // See: https://github.com/emotion-js/emotion/issues/1105#issuecomment-557726922
        cache.compat = true;

        setCache(cache);
        return () => {
            setCache(undefined);
            for (const tag of cache.sheet.tags) {
                tag.remove();
            }
        };
    }, [stylesRoot]);
    return cache;
}
